import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from '@/pages/LoginPage';
import LandingPage from '@/pages/LandingPage';
import DashboardPage from '@/pages/DashboardPage';
import IdleLogoutManager from '@/components/IdleLogoutManager';
import MFADeadlineBanner from '@/components/MFADeadlineBanner';
import ScanPage from '@/pages/ScanPage';
import ResultsPage from '@/pages/ResultsPage';
import APIKeysPage from '@/pages/APIKeysPage';
import APIDocsPage from '@/pages/APIDocsPage';
import SettingsPage from '@/pages/SettingsPage';
import UserManagementPage from '@/pages/UserManagementPage';
import WebhooksPage from '@/pages/WebhooksPage';
import MFASettingsPage from '@/pages/MFASettingsPage';
import DataManagementPage from '@/pages/DataManagementPage';
import OrganizationsPage from '@/pages/OrganizationsPage';
import BillingPage from '@/pages/BillingPage';
import AuditLogsPage from '@/pages/AuditLogsPage';
import ComplianceArchivePage from '@/pages/ComplianceArchivePage';
import UsageExportPage from '@/pages/UsageExportPage';
import OrgPortalPage from '@/pages/OrgPortalPage';
import DeepScanPage from '@/pages/DeepScanPage';
import DemoRequestsPage from '@/pages/DemoRequestsPage';
import { Toaster } from '@/components/ui/sonner';

// Saves the full deep link URL and redirects to login
function DeepScanRedirect() {
  const fullUrl = window.location.pathname + window.location.search;
  sessionStorage.setItem('deep_link_redirect', fullUrl);
  return <Navigate to="/login" />;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Send cookies on same-origin requests (httpOnly auth). On cross-origin production
// deployments where the ingress adds Allow-Origin:*, browsers reject credentialed
// responses — so we ALSO carry an Authorization: Bearer header from localStorage
// as a transparent fallback. Backend accepts whichever auth method arrives first.
axios.defaults.withCredentials = true;

// Attach Authorization header on every outgoing request when a legacy token exists.
axios.interceptors.request.use((config) => {
  const t = localStorage.getItem('token');
  if (t && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  // CSRF double-submit — echo the csrf_token cookie value in a header on
  // every unsafe request. The backend enforces this whenever the request
  // is cookie-authenticated (no Bearer token).
  try {
    const method = (config.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
      if (m && m[1]) {
        config.headers = config.headers || {};
        if (!config.headers['X-CSRF-Token']) {
          config.headers['X-CSRF-Token'] = decodeURIComponent(m[1]);
        }
      }
    }
  } catch (_e) { /* cookie parsing best-effort */ }
  return config;
});

// --- Axios interceptor for auto-refresh ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(p => {
    if (error) p.reject(error);
    else p.resolve();
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login') && !originalRequest.url?.includes('/auth/refresh') && !originalRequest.url?.includes('/auth/me')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        // Try cookie-based refresh first; if a legacy refresh_token is in localStorage, send it too.
        const legacyRefresh = localStorage.getItem('refresh_token');
        const { data } = await axios.post(`${API}/auth/refresh`, legacyRefresh ? { refresh_token: legacyRefresh } : {});
        if (data?.token) localStorage.setItem('token', data.token);
        if (data?.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Hybrid auth bootstrap:
    // 1) If a legacy token exists in localStorage, hydrate user immediately (fast path)
    // 2) Then validate session via /auth/me (works with cookie OR Bearer header)
    let cancelled = false;
    const cachedToken = localStorage.getItem('token');
    const cachedUser = localStorage.getItem('user');
    if (cachedToken && cachedUser) {
      try { setUser(JSON.parse(cachedUser)); } catch { /* unreadable cache; the server call below decides */ }
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (!cancelled) {
          setUser(data);
          localStorage.setItem('user', JSON.stringify(data));
        }
      } catch (e) {
        if (!cancelled) {
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogin = (token, userData, refreshToken) => {
    // Store BOTH (a) the access/refresh tokens in localStorage (for cross-origin Bearer auth)
    // AND (b) cookies are set server-side automatically (for same-origin secure auth).
    // Whichever the browser/network supports will work.
    if (token) localStorage.setItem('token', token);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);

    // Check for deep link redirect after login
    const deepRedirect = sessionStorage.getItem('deep_link_redirect');
    if (deepRedirect) {
      sessionStorage.removeItem('deep_link_redirect');
      window.location.href = deepRedirect;
    }
  };

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // server may already consider us logged out; ignore
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('mfa_enrollment_required');
    setUser(null);
  }, []);

  // Helper to check if user has admin-level access
  const isAdmin = user && ['super_admin', 'org_admin', 'admin'].includes(user.role);
  const isSuperAdmin = user && user.role === 'super_admin';
  // Privileged users who logged in without MFA — must complete enrollment
  // before accessing anything except /security and /login.
  const mfaEnrollmentPending = user && localStorage.getItem('mfa_enrollment_required') === 'true';

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen bg-slate-50">Loading...</div>;
  }

  // If a privileged user is stuck at enrollment, force them to /security
  // regardless of which route they try to visit.
  const guard = (element) => (mfaEnrollmentPending ? <Navigate to="/security" replace /> : element);

  return (
    <div className="App">
      {user && !mfaEnrollmentPending && <IdleLogoutManager user={user} onLogout={handleLogout} />}
      {user && !mfaEnrollmentPending && <MFADeadlineBanner user={user} />}
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={!user ? <LoginPage onLogin={handleLogin} /> : <Navigate to="/" />}
          />
          <Route
            path="/"
            element={user ? guard(<DashboardPage user={user} onLogout={handleLogout} />) : <LandingPage />}
          />
          <Route
            path="/scan"
            element={user ? guard(<ScanPage user={user} onLogout={handleLogout} />) : <Navigate to="/login" />}
          />
          <Route
            path="/results"
            element={user ? guard(<ResultsPage user={user} onLogout={handleLogout} />) : <Navigate to="/login" />}
          />
          <Route
            path="/integrations"
            element={isSuperAdmin ? guard(<APIKeysPage user={user} onLogout={handleLogout} />) : <Navigate to="/" />}
          />
          <Route
            path="/documentation"
            element={isSuperAdmin ? guard(<APIDocsPage user={user} onLogout={handleLogout} />) : <Navigate to="/" />}
          />
          <Route
            path="/settings"
            element={isSuperAdmin ? guard(<SettingsPage user={user} onLogout={handleLogout} />) : <Navigate to="/" />}
          />
          <Route
            path="/security"
            element={user ? <MFASettingsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/users"
            element={isAdmin ? guard(<UserManagementPage user={user} onLogout={handleLogout} />) : <Navigate to="/" />}
          />
          <Route
            path="/webhooks"
            element={user ? guard(<WebhooksPage user={user} onLogout={handleLogout} />) : <Navigate to="/login" />}
          />
          <Route
            path="/demo-requests"
            element={isSuperAdmin ? guard(<DemoRequestsPage user={user} onLogout={handleLogout} />) : <Navigate to="/" />}
          />
          <Route
            path="/data"
            element={isSuperAdmin ? guard(<DataManagementPage user={user} onLogout={handleLogout} />) : <Navigate to="/" />}
          />
          <Route
            path="/organizations"
            element={isSuperAdmin ? guard(<OrganizationsPage user={user} onLogout={handleLogout} />) : <Navigate to="/" />}
          />
          <Route
            path="/billing"
            element={user ? guard(<BillingPage user={user} onLogout={handleLogout} />) : <Navigate to="/login" />}
          />
          <Route
            path="/audit-logs"
            element={isAdmin ? guard(<AuditLogsPage user={user} onLogout={handleLogout} />) : <Navigate to="/" />}
          />
          <Route
            path="/compliance-archive"
            element={user ? guard(<ComplianceArchivePage user={user} onLogout={handleLogout} />) : <Navigate to="/login" />}
          />
          <Route
            path="/usage"
            element={isAdmin ? guard(<UsageExportPage user={user} onLogout={handleLogout} />) : <Navigate to="/" />}
          />
          <Route
            path="/org-portal"
            element={user ? guard(<OrgPortalPage user={user} onLogout={handleLogout} />) : <Navigate to="/login" />}
          />
          <Route
            path="/scan/deep"
            element={user ? guard(<DeepScanPage user={user} onLogout={handleLogout} />) : <DeepScanRedirect />}
          />
          <Route path="*" element={<Navigate to={user ? '/' : '/login'} />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;